import React, { useState } from 'react';
import { X, ArrowUpFromLine, Loader2, Wallet, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { withdrawApi } from '../api';
import usePhantom from '../hooks/usePhantom';

const WithdrawModal = ({ open, onClose }) => {
  const { user, refresh } = useAuth();
  const phantom = usePhantom();
  const [amount, setAmount] = useState('');
  const [destination, setDestination] = useState(user?.walletAddress || '');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const balance = Number(user?.walletBalance || 0);
  const airdropBalance = Number(user?.airdropBalance || 0);
  const withdrawable = balance; // airdrop is excluded (non-withdrawable until TGE)

  if (!open) return null;

  const handleConnectPhantom = async () => {
    setError(null);
    try {
      const pk = await phantom.connect();
      setDestination(pk);
    } catch (e) {
      setError(e?.message || 'Phantom connect failed');
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    const amt = parseFloat(amount);
    if (!(amt > 0)) { setError('Montant invalide.'); return; }
    if (amt > withdrawable) { setError(`Solde retirable insuffisant (${withdrawable.toFixed(2)} DOMIA). L'airdrop est verrouillé jusqu'au TGE.`); return; }
    const dest = (destination || '').trim();
    if (!dest || dest.length < 32) { setError('Adresse Solana invalide.'); return; }
    setLoading(true);
    try {
      const res = await withdrawApi.withdraw({ amount: amt, destinationWallet: dest });
      setSuccess(res);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Withdraw failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 sm:px-4 py-6" onClick={onClose} data-testid="withdraw-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-slate-900 to-blue-900 text-white flex justify-between items-center">
          <div className="flex items-center gap-2">
            <ArrowUpFromLine className="w-5 h-5" />
            <div>
              <div className="text-[11px] font-bold opacity-80 uppercase tracking-widest">Withdraw</div>
              <div className="text-lg font-black">Send DOMIA to your wallet</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full" data-testid="withdraw-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {!success && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-3 py-2">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700">Withdrawable</div>
                  <div className="text-base font-black text-slate-900 mt-0.5">{withdrawable.toFixed(2)} DOMIA</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" title="Airdrop locked until TGE">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">🔒 Airdrop</div>
                  <div className="text-base font-black text-amber-700 mt-0.5">{airdropBalance.toFixed(2)} DOMIA</div>
                </div>
              </div>

              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 block mb-1.5">Amount (DOMIA)</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    data-testid="withdraw-amount-input"
                    className="flex-1 border-2 border-slate-200 rounded-xl px-3 py-2.5 text-[15px] font-bold focus:outline-none focus:border-blue-500"
                  />
                  <button onClick={() => setAmount(String(withdrawable.toFixed(2)))} className="px-3 rounded-xl bg-slate-100 hover:bg-slate-200 font-bold text-[12px]">MAX</button>
                </div>
              </div>

              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 block mb-1.5">Destination wallet</label>
                <input
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="Paste your Solana wallet address…"
                  data-testid="withdraw-address-input"
                  className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-[12px] font-mono focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={handleConnectPhantom}
                  data-testid="withdraw-connect-phantom"
                  className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl py-2.5 font-extrabold text-[12px] bg-gradient-to-r from-purple-600 to-purple-800 text-white shadow-md hover:opacity-90 disabled:opacity-50"
                  disabled={phantom.connecting}
                >
                  <Wallet className="w-4 h-4" />
                  {phantom.connected ? `Phantom: ${phantom.publicKey.slice(0,4)}…${phantom.publicKey.slice(-4)}` : (phantom.available ? 'Auto-fill from Phantom' : 'Install Phantom')}
                </button>
              </div>

              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-[12px] font-bold text-red-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  {error}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading}
                data-testid="withdraw-submit-btn"
                className="w-full rounded-xl py-3 font-black text-white text-[14px] tracking-widest uppercase shadow-lg disabled:opacity-50"
                style={{ background: 'linear-gradient(90deg, #0f172a, #1e3a8a)' }}
              >
                {loading ? <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> : null}
                {loading ? 'Sending…' : 'Confirm Withdraw'}
              </button>

              <p className="text-[10px] text-slate-500 text-center">
                🔒 Les jetons Airdrop ({airdropBalance.toFixed(0)} DOMIA) sont <strong>verrouillés jusqu'au TGE</strong> (fin du presale). Seul le solde principal est retirable.
              </p>
            </>
          )}

          {success && (
            <div className="text-center space-y-3 py-2" data-testid="withdraw-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
              <div className="text-xl font-black text-slate-900">Retrait confirmé !</div>
              <div className="text-[13px] text-slate-600">
                {success.tx?.length ? `${success.tx.length} tx envoyée(s) sur Solana mainnet.` : 'Transaction soumise.'}
              </div>
              {success.tx?.[0]?.sig && (
                <a href={`https://solscan.io/tx/${success.tx[0].sig}`} target="_blank" rel="noreferrer" className="text-[12px] font-mono text-blue-600 break-all">
                  {success.tx[0].sig.slice(0, 24)}…
                </a>
              )}
              <button onClick={onClose} className="w-full rounded-xl py-2.5 font-extrabold text-white bg-blue-600">Fermer</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WithdrawModal;
